// FILE: src/pages/Documents/DocumentsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { documentsAPI, clientsAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Documents.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';
const FILE_ICONS = { pdf:'📄', doc:'📝', docx:'📝', jpg:'🖼️', jpeg:'🖼️', png:'🖼️', xls:'📊', xlsx:'📊', default:'📎' };
const getIcon = (path) => { const ext = (path||'').split('.').pop().toLowerCase(); return FILE_ICONS[ext] || FILE_ICONS.default; };

export default function DocumentsPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [docs,      setDocs]      = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [clientFilt,setClientFilt]= useState('');
  const [showUpload,setShowUpload]= useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dRes, cRes] = await Promise.all([documentsAPI.getAll(), clientsAPI.getAll()]);
      setDocs(dRes.data.data   || []);
      setFiltered(dRes.data.data || []);
      setClients(cRes.data.data  || []);
    } catch { toast.error('Failed to load documents.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...docs];
    if (clientFilt) data = data.filter(d => String(d.ClientID) === clientFilt);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(d =>
        (d.DocumentName||'').toLowerCase().includes(q) ||
        (d.DocumentType||'').toLowerCase().includes(q) ||
        (d.ClientName||'').toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, clientFilt, docs]);

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await documentsAPI.delete(deleting.ID);
      toast.success('Document deleted.');
      setDeleting(null); load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Documents</h1>
          <p>{docs.length} documents uploaded</p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search document name, type…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:220}}/>
          </div>
          <select className="form-control" style={{width:200}} value={clientFilt}
            onChange={e=>setClientFilt(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
          </select>
          {canEdit && (
            <button className="btn btn-primary" onClick={()=>setShowUpload(true)}>📤 Upload Document</button>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Total Documents', value:docs.length,                                                          icon:'📁'},
          {label:'PDFs',            value:docs.filter(d=>(d.FilePath||'').endsWith('.pdf')).length,             icon:'📄'},
          {label:'Images',          value:docs.filter(d=>/(jpg|jpeg|png)$/i.test(d.FilePath||'')).length,       icon:'🖼️'},
          {label:'Expiring Soon',   value:docs.filter(d=>{
            if (!d.ExpirationDate) return false;
            return Math.ceil((new Date(d.ExpirationDate)-new Date())/86400000) <= 30;
          }).length, icon:'⚠️'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">⏳ Loading documents…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📁</div>
          <div className="empty-state-text">{search||clientFilt ? 'No documents match.' : 'No documents uploaded yet.'}</div>
          {!search && !clientFilt && canEdit && (
            <button className="btn btn-primary" style={{marginTop:14}} onClick={()=>setShowUpload(true)}>📤 Upload First Document</button>
          )}
        </div>
      ) : (
        <div className="doc-grid">
          {filtered.map(d=>{
            const expiring = d.ExpirationDate && Math.ceil((new Date(d.ExpirationDate)-new Date())/86400000) <= 30;
            const expired  = d.ExpirationDate && new Date(d.ExpirationDate) < new Date();
            return (
              <div key={d.ID} className={`doc-card${expired?' expired':expiring?' expiring':''}`}>
                <div className="doc-icon">{getIcon(d.FilePath)}</div>
                <div className="doc-info">
                  <div className="doc-name">{d.DocumentName}</div>
                  <div className="doc-client">{d.ClientName||'—'}</div>
                  <div className="doc-meta">
                    <span className="badge badge-gray">{d.DocumentType||'Document'}</span>
                    <span style={{fontSize:'0.72rem',color:'var(--gray-400)'}}>
                      Uploaded {fmtDate(d.UploadDate)}
                    </span>
                  </div>
                  {d.ExpirationDate && (
                    <div style={{fontSize:'0.72rem',marginTop:4}}>
                      <span className={`badge ${expired?'badge-danger':expiring?'badge-warning':'badge-success'}`}>
                        {expired?'Expired':'Expires'}: {fmtDate(d.ExpirationDate)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="doc-actions">
                  {d.FilePath && (
                    <a href={`http://localhost:5000/${d.FilePath}`} target="_blank" rel="noreferrer"
                      className="btn btn-sm btn-secondary">📎 View</a>
                  )}
                  {canEdit && (
                    <button className="btn btn-sm btn-danger" onClick={()=>setDeleting(d)}>🗑️</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <UploadModal clients={clients} onClose={()=>setShowUpload(false)} onSaved={()=>{ setShowUpload(false); load(); }}/>
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header"><span className="modal-title">🗑️ Delete Document</span><button className="modal-close" onClick={()=>setDeleting(null)}>✕</button></div>
            <div className="modal-body"><p style={{color:'var(--gray-600)'}}>Delete <strong>{deleting.DocumentName}</strong>? The file will be permanently removed.</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>{delLoad?'Deleting…':'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadModal({ clients, onClose, onSaved }) {
  const [form,     setForm]     = useState({ ClientID:'', DocumentName:'', DocumentType:'', ExpirationDate:'' });
  const [file,     setFile]     = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [progress, setProgress] = useState(0);
  const set = (f,v) => setForm(p=>({...p,[f]:v}));

  const handleUpload = async () => {
    if (!form.ClientID || !form.DocumentName || !file) { toast.error('Client, name, and file are required.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if(v) fd.append(k,v); });
      fd.append('document', file);
      await documentsAPI.upload(fd);
      toast.success('Document uploaded successfully.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Upload failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">📤 Upload Document</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className="form-control" value={form.ClientID} onChange={e=>set('ClientID',e.target.value)}>
                <option value="">— Select —</option>
                {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Document Type</label>
              <select className="form-control" value={form.DocumentType} onChange={e=>set('DocumentType',e.target.value)}>
                <option value="">— Select —</option>
                {['Assessment','Care Plan','Insurance Card','ID/Driver\'s License','Medicaid Authorization',
                  'Medical Record','Progress Note','Signed Agreement','Other'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Document Name <span className="req">*</span></label>
              <input className="form-control" value={form.DocumentName} onChange={e=>set('DocumentName',e.target.value)} placeholder="e.g. Medicaid Card 2025"/>
            </div>
            <div className="form-group">
              <label className="form-label">Expiration Date</label>
              <input className="form-control" type="date" value={form.ExpirationDate} onChange={e=>set('ExpirationDate',e.target.value)}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">File <span className="req">*</span></label>
            <div className={`file-drop${file?' has-file':''}`}
              onDragOver={e=>e.preventDefault()}
              onDrop={e=>{ e.preventDefault(); const f=e.dataTransfer.files[0]; if(f) setFile(f); }}>
              {file ? (
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'2rem'}}>{getIcon(file.name)}</div>
                  <div style={{fontWeight:600,marginTop:6}}>{file.name}</div>
                  <div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{(file.size/1024/1024).toFixed(2)} MB</div>
                  <button className="btn btn-sm btn-secondary" style={{marginTop:8}} onClick={()=>setFile(null)}>✕ Remove</button>
                </div>
              ) : (
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'2.5rem',marginBottom:8}}>📁</div>
                  <div style={{fontWeight:600,color:'var(--gray-600)'}}>Drop file here or click to browse</div>
                  <div style={{fontSize:'0.78rem',color:'var(--gray-400)',marginTop:4}}>PDF, Word, Images, Excel</div>
                </div>
              )}
              <input type="file" style={{position:'absolute',inset:0,opacity:0,cursor:'pointer'}}
                onChange={e=>{ if(e.target.files[0]) setFile(e.target.files[0]); }}/>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={saving}>{saving?'Uploading…':'📤 Upload'}</button>
        </div>
      </div>
    </div>
  );
}